/* ==========================================================================
   authService

   MOCK MODE: simulated in-memory login/register against mock/mockDb.

   REAL MODE (Supabase): auth.users + the `profiles` table, wired up by
   supabase/schema.sql. A few things worth knowing about the real branch:

   - Registration requires Supabase's own "Confirm email" setting to be
     OFF for new users to be logged in immediately after signing up
     (Authentication -> Providers -> Email in the Supabase dashboard). If
     it's on, signUp() succeeds but returns no session, and register()
     below surfaces a clear "check your email" error instead of silently
     failing to log the user in.
   - Password reset uses Supabase's real emailed-link flow, not a manually
     typed code: requestPasswordReset() sends the email; the resident
     clicks the link, lands back on the app, and main.ts detects Supabase's
     PASSWORD_RECOVERY auth event and routes to the reset-confirm screen
     automatically. confirmPasswordReset() then just sets the new password
     on that recovery session — the "Reset Code" field in that screen is
     unused in real mode (harmless, just ignored).
   - New accounts are auto-assigned to the single starter estate created by
     schema.sql (see that file's note on multi-estate support).
   ========================================================================== */

import { config } from "../config";
import { getSupabase } from "./supabaseClient";
import { fetchProfileRow } from "./supabaseMappers";
import { mockDb } from "./mock/mockDb";
import { delay, genReference } from "./mock/mockUtils";
import { sessionStore } from "../state/session";
import { estateContext } from "../state/estateContext";
import { appStore } from "../state/store";
import type {
  LoginCredentials,
  PasswordResetConfirm,
  PasswordResetRequest,
  RegisterPayload,
  Session,
} from "../types";

export class AuthError extends Error {}

async function login(credentials: LoginCredentials): Promise<Session> {
  if (config.useMockServices) {
    await delay(600);
    const record = mockDb.findUserByEmail(credentials.email);
    if (!record || record.password !== credentials.password) {
      throw new AuthError("Incorrect email or password.");
    }
    const session: Session = {
      user: mockDb.toAuthUser(record),
      token: `mock_${genReference("TKN")}`,
      expiresAt: new Date(Date.now() + 1000 * 60 * 60 * 12).toISOString(),
    };
    sessionStore.set(session);
    return session;
  }

  const supabase = getSupabase();
  const { data, error } = await supabase.auth.signInWithPassword({
    email: credentials.email,
    password: credentials.password,
  });
  if (error) throw new AuthError(error.message);
  if (!data.session || !data.user) throw new AuthError("Sign-in did not return a session.");

  const user = await fetchProfileRow(data.user.id);
  const session: Session = {
    user,
    token: data.session.access_token,
    expiresAt: new Date(data.session.expires_at! * 1000).toISOString(),
    supabaseAccessToken: data.session.access_token,
    supabaseRefreshToken: data.session.refresh_token,
  };
  sessionStore.set(session);
  return session;
}

async function register(payload: RegisterPayload): Promise<Session> {
  if (config.useMockServices) {
    await delay(700);
    if (mockDb.findUserByEmail(payload.email)) {
      throw new AuthError("An account with this email already exists.");
    }
    const record = mockDb.createUser({
      name: payload.name,
      email: payload.email,
      phone: payload.phone,
      password: payload.password,
      role: "user",
    });
    const session: Session = {
      user: mockDb.toAuthUser(record),
      token: `mock_${genReference("TKN")}`,
      expiresAt: new Date(Date.now() + 1000 * 60 * 60 * 12).toISOString(),
    };
    sessionStore.set(session);
    return session;
  }

  const supabase = getSupabase();
  const { data, error } = await supabase.auth.signUp({
    email: payload.email,
    password: payload.password,
    options: { data: { name: payload.name, phone: payload.phone } },
  });
  if (error) throw new AuthError(error.message);
  if (!data.user) throw new AuthError("Registration did not complete.");
  if (!data.session) {
    throw new AuthError("Account created \u2014 check your email to confirm it, then sign in.");
  }

  const user = await fetchProfileRow(data.user.id);
  const session: Session = {
    user,
    token: data.session.access_token,
    expiresAt: new Date(data.session.expires_at! * 1000).toISOString(),
    supabaseAccessToken: data.session.access_token,
    supabaseRefreshToken: data.session.refresh_token,
  };
  sessionStore.set(session);
  return session;
}

async function logout(): Promise<void> {
  if (!config.useMockServices) {
    try {
      await getSupabase().auth.signOut();
    } catch {
      // Logging out client-side should proceed even if the network call
      // fails — the user still expects to be signed out locally.
    }
  }
  sessionStore.clear();
  estateContext.resetToOwnEstate();
  appStore.reset();
}

async function requestPasswordReset(payload: PasswordResetRequest): Promise<void> {
  if (config.useMockServices) {
    await delay(600);
    // Intentionally resolves whether or not the email exists, matching
    // real backend behaviour that avoids confirming account existence.
    return;
  }
  try {
    await getSupabase().auth.resetPasswordForEmail(payload.email, {
      redirectTo: window.location.origin + "/index.html",
    });
  } catch {
    // Same reasoning as mock mode: don't leak whether the email exists.
  }
}

async function confirmPasswordReset(payload: PasswordResetConfirm): Promise<void> {
  if (config.useMockServices) {
    await delay(600);
    if (!payload.token) throw new AuthError("Reset link is invalid or has expired.");
    return;
  }
  // The "token" field is unused here — real mode relies on the recovery
  // session Supabase establishes automatically when the resident clicks
  // the emailed reset link (see the module note above).
  const supabase = getSupabase();
  const { error } = await supabase.auth.updateUser({ password: payload.newPassword });
  if (error) throw new AuthError(error.message);
  await supabase.auth.signOut();
}

export const authService = {
  login,
  register,
  logout,
  requestPasswordReset,
  confirmPasswordReset,
};
